import { useEffect } from 'react';

/**
 * Houdt het scherm aan zolang het gesprek loopt.
 *
 * WAAROM DIT GEEN LUXE IS MAAR EEN REPARATIE (11-09-2026). In een tolkgesprek
 * praat je; je raakt het scherm niet aan. Een iPhone dooft dan na een minuut
 * of twee en gaat op slot. Dat is voor iOS een ONDERBREKING van de
 * geluidssessie: de AudioContext gaat naar 'interrupted' en er komt geen
 * geluid meer uit tot je opnieuw tikt.
 *
 * Giovanni liep daar tegenaan op zijn iPhone: de oranje balk bleef terugkomen,
 * en tussendoor sprong het toestel naar het vergrendelscherm. Het geluid
 * repareren zonder dit erbij is dweilen: de onderbreking komt gewoon terug.
 *
 * DE VERGRENDELING VAN DE GEBRUIKER WORDT NIET OVERRULED. Dit houdt alleen de
 * automatische schermtijd tegen. Drukt iemand zelf op de knop, dan gaat het
 * scherm gewoon uit, en de browser geeft de reservering dan zelf op.
 *
 * OPNIEUW VRAGEN NA TERUGKOMST IS VERPLICHT. De browser trekt de reservering
 * in zodra het tabblad naar de achtergrond gaat, en geeft hem NIET vanzelf
 * terug. Zonder de luisteraar op `visibilitychange` werkt dit precies één keer
 * - en dat is dezelfde stille faalvorm als de ontgrendel-luisteraar die
 * zichzelf weghaalde. Zie useAudioPlayer.ts.
 *
 * EN DAT WAS NOG NIET GENOEG (11-09-2026, tweede ronde). Giovanni meldde dat
 * het scherm alsnog zwart werd als hij een paar seconden wachtte voor hij zijn
 * antwoord verstuurde. Een browser mag een reservering namelijk OOK tussendoor
 * intrekken, zonder dat het tabblad van zichtbaarheid verandert - bij een
 * lage batterij, bij een energiestand, of gewoon uit zichzelf. Daarom hangen er
 * nu drie wegen terug naar binnen:
 *
 *   1. `visibilitychange`, voor terugkomst uit de achtergrond
 *   2. de gebeurtenis `release` op de reservering zelf, voor een intrekking
 *      die de browser uit zichzelf doet
 *   3. een trage klok, als vangnet voor allebei de gevallen die iemand ooit
 *      vergeet - hij kijkt alleen en vraagt niets zolang de reservering staat
 */

interface SchermReservering {
  release: () => Promise<void>;
  released: boolean;
  addEventListener: (soort: 'release', luisteraar: () => void) => void;
}

interface WakeLockApi {
  request: (soort: 'screen') => Promise<SchermReservering>;
}

function api(): WakeLockApi | null {
  const nav = navigator as Navigator & { wakeLock?: WakeLockApi };
  return nav.wakeLock ?? null;
}

/** Is deze browser hiertoe in staat? Voor een melding op het scherm. */
export function schermWakkerBeschikbaar(): boolean {
  return api() !== null;
}

export function useSchermWakker(aan: boolean): void {
  useEffect(() => {
    if (!aan) return;
    const wakeLock = api();
    // Geen API: niets te doen, en geen melding. Het gesprek werkt gewoon, het
    // scherm dooft alleen. Firefox op Android en oudere iOS hebben dit niet.
    if (wakeLock === null) return;

    let reservering: SchermReservering | null = null;
    let gestopt = false;

    const vraag = (): void => {
      // Alleen vragen als de pagina zichtbaar is. De browser weigert het
      // anders, en dat levert een fout op die niets betekent.
      if (gestopt || document.visibilityState !== 'visible') return;
      if (reservering !== null && !reservering.released) return;

      void wakeLock
        .request('screen')
        .then((nieuw) => {
          if (gestopt) {
            void nieuw.release();
            return;
          }
          reservering = nieuw;
          // De browser kan hem uit zichzelf intrekken. Dan meteen opnieuw
          // vragen; staat de pagina op de achtergrond, dan wacht vraag() af.
          nieuw.addEventListener('release', () => {
            vraag();
          });
        })
        .catch(() => {
          // Geweigerd, bijvoorbeeld omdat de batterij bijna leeg is. Dat is
          // geen storing en hoort de gebruiker niet te lezen: het gesprek gaat
          // gewoon door, het scherm dooft alleen weer.
        });
    };

    const opZichtbaar = (): void => vraag();

    vraag();
    document.addEventListener('visibilitychange', opZichtbaar);
    // Het vangnet. Twintig seconden is traag genoeg om niets te kosten en snel
    // genoeg om een zwart scherm niet te laten staan: de klok doet alleen een
    // vergelijking zolang de reservering staat, en vraagt pas iets als hij weg
    // is.
    const klok = window.setInterval(vraag, 20_000);

    return () => {
      gestopt = true;
      window.clearInterval(klok);
      document.removeEventListener('visibilitychange', opZichtbaar);
      if (reservering !== null && !reservering.released) {
        void reservering.release().catch(() => {
          // De browser had hem al ingetrokken. Niets aan de hand.
        });
      }
    };
  }, [aan]);
}
